<template>
  <div data-test-id="record-restriction-fields">
    <div class="row items-center">
      <div class="col-auto">
        Allow <strong>{{ rolePermission.label }}</strong> on
      </div>
      <div class="col-auto">
        <q-select
          v-model="restrictionType"
          class="q-ml-sm"
          :options="restrictionTypeOptions"
          map-options
          emit-value
          dense
          :bg-color="isRestrictionsEnabled ? 'amber-1' : undefined"
          :label-color="isRestrictionsEnabled ? 'amber-9' : undefined"
        />
      </div>
    </div>
    <div class="row">
      <div class="col">
        <model-search-select
          :model-value="restrictedRecordIds"
          :model="modelClass"
          :placeholder="isRestrictionsEnabled ? `Type to search for ${humanModelName}` : 'No record restrictions'"
          :disable="!isRestrictionsEnabled"
          require-value
          @update:model-value="handleModelSearchSelectInput"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ModelSearchSelect from 'src/modules/admin/role-permission/ModelSearchSelect'

const RESTRICTION_TYPE_OPTIONS = {
  NO_RESTRICTIONS: 'no-restrictions',
  ONLY: 'only',
  EXCEPT: 'except'
}

export default {
  name: 'RecordRestrictionFields',
  components: {
    ModelSearchSelect
  },
  props: {
    rolePermission: {
      type: Object,
      required: true
    },
    modelClass: {
      type: Function,
      required: true
    }
  },
  data () {
    return {
      restrictionType: this.inferRestrictionType()
    }
  },
  computed: {
    humanModelName () {
      return this.modelClass.humanTypeName
    },
    isRestrictionsEnabled () {
      return this.restrictionType !== RESTRICTION_TYPE_OPTIONS.NO_RESTRICTIONS
    },
    recordRestrictions () {
      return _.filter(this.rolePermission.rolePermissionRecordRestrictions.data, { restrictableType: this.modelClass.name })
    },
    restrictedRecordIds () {
      return _.map(this.recordRestrictions, 'restrictableId')
    },
    restrictionTypeOptions () {
      return [
        { label: `any ${this.humanModelName} record`, value: RESTRICTION_TYPE_OPTIONS.NO_RESTRICTIONS },
        { label: `only specific ${this.humanModelName} records`, value: RESTRICTION_TYPE_OPTIONS.ONLY },
        { label: `all ${this.humanModelName} records except`, value: RESTRICTION_TYPE_OPTIONS.EXCEPT }
      ]
    }
  },
  watch: {
    restrictionType () {
      if (this.restrictionType === RESTRICTION_TYPE_OPTIONS.NO_RESTRICTIONS) {
        // Empty the record restrictions
        _.each(this.recordRestrictions, (recordRestriction) => {
          this.rolePermission.rolePermissionRecordRestrictions.remove(recordRestriction)
        })
        return
      }

      let excludedTypes = this.rolePermission.data.conditionsRequirements.excludeRestrictableTypes
      if (this.restrictionType === RESTRICTION_TYPE_OPTIONS.ONLY) {
        excludedTypes = _.without(excludedTypes, this.modelClass.name)
      } else if (this.restrictionType === RESTRICTION_TYPE_OPTIONS.EXCEPT) {
        excludedTypes = _(excludedTypes).union([this.modelClass.name]).sort().value()
      }
      this.rolePermission.data.conditionsRequirements.excludeRestrictableTypes = excludedTypes
    }
  },
  methods: {
    handleModelSearchSelectInput (selectedRecordIds) {
      const idsToAdd = _.difference(selectedRecordIds, this.restrictedRecordIds)
      _.each(idsToAdd, (id) => {
        this.rolePermission.rolePermissionRecordRestrictions.add({ restrictableId: id, restrictableType: this.modelClass.name })
      })
      // Remove any record restrictions of the current type that are no longer selected
      _.each(this.recordRestrictions, (recordRestriction) => {
        if (!_.includes(selectedRecordIds, recordRestriction.restrictableId)) {
          this.rolePermission.rolePermissionRecordRestrictions.remove(recordRestriction)
        }
      })
    },
    inferRestrictionType () {
      if (_.filter(this.rolePermission.rolePermissionRecordRestrictions.data, { restrictableType: this.modelClass.name }).length === 0) {
        return RESTRICTION_TYPE_OPTIONS.NO_RESTRICTIONS
      } else if (this.rolePermission.excludesRestrictedRecords(this.modelClass.name)) {
        return RESTRICTION_TYPE_OPTIONS.EXCEPT
      } else {
        return RESTRICTION_TYPE_OPTIONS.ONLY
      }
    }
  }
}
</script>
